import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { downloadCSV, inr, EmptyRow } from "./utils";

const INTERNAL_DEPS = [
  "Awaiting Die Purchase",
  "Plates to be ordered",
  "Plates awaited (ordered)",
  "Color separation approval awaited",
  "RM to be purchased",
];
const CUSTOMER_DEPS = [
  "Shade card approval awaited",
  "PO awaited",
  "HPDF approval awaited",
];

export default function DependencyListReport() {
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    const params = {};
    if (customerFilter !== "all") params.customer_name = customerFilter;
    if (typeFilter !== "all") params.dependency_type = typeFilter;
    if (reasonFilter !== "all") params.dependency_reason = reasonFilter;
    api.get("/reports/dependency-list", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerFilter, typeFilter, reasonFilter]);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) =>
        (r.customer_name || "").toLowerCase().includes(n) ||
        (r.bpr_number || "").toLowerCase().includes(n) ||
        (r.product_code || "").toLowerCase().includes(n) ||
        (r.description || "").toLowerCase().includes(n)
      );
    }
    return list;
  }, [data, q]);

  // Group by (dependency_type, dependency_reason) for the mockup layout
  const groups = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const key = `${r.dependency_type}||${r.dependency_reason}`;
      if (!map[key]) map[key] = { type: r.dependency_type, reason: r.dependency_reason, rows: [] };
      map[key].rows.push(r);
    }
    // Order: Internal first (in list order), then Customer
    const ordered = [];
    INTERNAL_DEPS.forEach((rn) => { const k = `Internal Dependency||${rn}`; if (map[k]) ordered.push(map[k]); });
    CUSTOMER_DEPS.forEach((rn) => { const k = `Customer Dependency||${rn}`; if (map[k]) ordered.push(map[k]); });
    return ordered;
  }, [rows]);

  const summary = data?.summary || { "Internal Dependency": {}, "Customer Dependency": {} };
  const totalPending = data?.total_pending || 0;

  const exportCSV = () => downloadCSV(
    "dependency-list",
    ["Type of Dependency", "Reason", "BPR Date", "BPR Number", "Customer Name", "Product Code", "Description", "Quantity", "Status"],
    rows.map((r) => [
      r.dependency_type, r.dependency_reason,
      (r.bpr_date || "").slice(0, 10), r.bpr_number, r.customer_name,
      r.product_code, r.description, r.quantity, r.status,
    ])
  );

  const totalInternal = Object.values(summary["Internal Dependency"] || {}).reduce((a, b) => a + b, 0);
  const totalCustomer = Object.values(summary["Customer Dependency"] || {}).reduce((a, b) => a + b, 0);

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Customer</label>
          <div className="flex items-center gap-1">
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger data-testid="dep-customer" className="rounded-sm w-60"><SelectValue placeholder="All customers" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {customerFilter !== "all" && (
              <button data-testid="dep-clear-customer" onClick={() => setCustomerFilter("all")}
                className="p-1.5 hover:bg-neutral-100 rounded-sm" title="Clear"><X size={14} /></button>
            )}
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">Type of Dependency</label>
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setReasonFilter("all"); }}>
            <SelectTrigger data-testid="dep-type" className="rounded-sm w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="Internal Dependency">Internal Dependency</SelectItem>
              <SelectItem value="Customer Dependency">Customer Dependency</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1">Reason</label>
          <Select value={reasonFilter} onValueChange={setReasonFilter}>
            <SelectTrigger data-testid="dep-reason" className="rounded-sm w-60"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reasons</SelectItem>
              {(typeFilter === "Customer Dependency" ? CUSTOMER_DEPS
                : typeFilter === "Internal Dependency" ? INTERNAL_DEPS
                : [...INTERNAL_DEPS, ...CUSTOMER_DEPS])
                .map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-48">
          <label className="label-caps block mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="dep-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm" placeholder="customer / BPR / product" />
          </div>
        </div>
        <Button data-testid="dep-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Apply"}
        </Button>
        <Button data-testid="dep-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      {/* Summary matrix — Internal + Customer tiles */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="border border-neutral-200 bg-white">
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
            <span className="label-caps text-amber-900 text-[10px]">Internal Dependency</span>
            <span className="font-mono-num text-lg font-bold text-amber-900">{inr(totalInternal)}</span>
          </div>
          <div className="divide-y divide-neutral-100">
            {INTERNAL_DEPS.map((r) => {
              const c = summary["Internal Dependency"]?.[r] || 0;
              return (
                <button key={r}
                  onClick={() => { setTypeFilter("Internal Dependency"); setReasonFilter(r); }}
                  className={`w-full flex justify-between items-center px-4 py-2 text-sm hover:bg-neutral-50 ${
                    reasonFilter === r ? "bg-orange-50" : ""
                  }`}
                  data-testid={`dep-tile-internal-${r}`}>
                  <span className={c === 0 ? "text-neutral-400" : "text-neutral-900"}>{r}</span>
                  <span className={`font-mono-num font-semibold ${c === 0 ? "text-neutral-300" : "text-amber-700"}`}>{c}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="border border-neutral-200 bg-white">
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between">
            <span className="label-caps text-blue-900 text-[10px]">Customer Dependency</span>
            <span className="font-mono-num text-lg font-bold text-blue-900">{inr(totalCustomer)}</span>
          </div>
          <div className="divide-y divide-neutral-100">
            {CUSTOMER_DEPS.map((r) => {
              const c = summary["Customer Dependency"]?.[r] || 0;
              return (
                <button key={r}
                  onClick={() => { setTypeFilter("Customer Dependency"); setReasonFilter(r); }}
                  className={`w-full flex justify-between items-center px-4 py-2 text-sm hover:bg-neutral-50 ${
                    reasonFilter === r ? "bg-orange-50" : ""
                  }`}
                  data-testid={`dep-tile-customer-${r}`}>
                  <span className={c === 0 ? "text-neutral-400" : "text-neutral-900"}>{r}</span>
                  <span className={`font-mono-num font-semibold ${c === 0 ? "text-neutral-300" : "text-blue-700"}`}>{c}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs text-neutral-500">
          <b className="text-neutral-900">{totalPending}</b> pending orders classified across {groups.length} dependency reasons.
        </div>
      </div>

      {/* Grouped detail — mimics the Excel layout with type + reason headers */}
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="dep-table">
          <thead className="bg-neutral-50 label-caps text-[10px] sticky top-0">
            <tr>
              <th className="text-left px-3 py-2.5 w-52">Type of Dependency</th>
              <th className="text-left px-3 py-2.5 w-64">Reason</th>
              <th className="text-left px-3 py-2.5">BPR Date</th>
              <th className="text-left px-3 py-2.5">BPR Number</th>
              <th className="text-left px-3 py-2.5">Customer Name</th>
              <th className="text-left px-3 py-2.5">Product Code</th>
              <th className="text-left px-3 py-2.5">Description</th>
              <th className="text-right px-3 py-2.5">Qty</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && <EmptyRow cols={8} msg="No jobs match this dependency filter." />}
            {groups.map((g) => (
              <DepGroupRows key={`${g.type}-${g.reason}`} group={g} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        Dependency is derived from the job's <b>status</b> and milestone fields (hpdf_sent / shade_card_sent / plates_ordered / die_available).
        For finer control, edit a job's manual <b>dependency_type</b> / <b>dependency_reason</b> via <code>PATCH /api/reports/jobs/&#123;id&#125;/dependency</code>.
      </p>
    </div>
  );
}

function DepGroupRows({ group }) {
  const [open, setOpen] = useState(true);
  const isCustomer = group.type === "Customer Dependency";
  return (
    <>
      <tr className={`border-t-2 border-neutral-200 ${isCustomer ? "bg-blue-50/40" : "bg-amber-50/40"} cursor-pointer`}
        onClick={() => setOpen(!open)}
        data-testid={`dep-group-${group.type}-${group.reason}`}>
        <td className="px-3 py-2 font-semibold uppercase tracking-widest text-[10px]">
          <button className="flex items-center gap-1.5">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className={isCustomer ? "text-blue-900" : "text-amber-900"}>{group.type}</span>
          </button>
        </td>
        <td className="px-3 py-2 font-semibold text-neutral-900" colSpan={5}>{group.reason}</td>
        <td className="px-3 py-2 text-right label-caps text-[10px]">{group.rows.length} JOB{group.rows.length !== 1 && "S"}</td>
        <td></td>
      </tr>
      {open && group.rows.slice(0, 500).map((r) => (
        <tr key={r.job_id} className="border-t border-neutral-100 data-row" data-testid={`dep-row-${r.job_id}`}>
          <td></td><td></td>
          <td className="px-3 py-1.5 font-mono-num text-xs text-neutral-600">{(r.bpr_date || "").slice(0, 10) || "—"}</td>
          <td className="px-3 py-1.5 font-mono-num text-xs">{r.bpr_number || "—"}</td>
          <td className="px-3 py-1.5 font-medium">{r.customer_name}</td>
          <td className="px-3 py-1.5 font-mono-num text-xs text-neutral-600">{r.product_code || "—"}</td>
          <td className="px-3 py-1.5 text-xs">{r.description || "—"}</td>
          <td className="px-3 py-1.5 text-right font-mono-num">{inr(r.quantity || 0)}</td>
        </tr>
      ))}
      {open && group.rows.length > 500 && (
        <tr><td colSpan={8} className="px-3 py-2 text-center text-xs text-neutral-400">+ {group.rows.length - 500} more rows in CSV export…</td></tr>
      )}
    </>
  );
}



// ============================================================================
// Inquiry Conversion Report — funnel of New/Repeat Inquiries → Orders
// ============================================================================
